import { useCallback, useEffect, useRef } from "react";
import { FileRejection } from "react-dropzone";
import { useFormContext, useWatch } from "react-hook-form";
import { v4 as uuid } from "uuid";

import { fileUtil } from "core/util";
import { FileItem, FileItemStatusEnum } from "types";

export function useFileUploadField(
    name: string,
    options?: {
        onLoad?: (file: FileItem) => Promise<Partial<FileItem>>;
        onDelete?: (file: FileItem) => Promise<void>;
    },
) {
    const { setValue, setError, clearErrors } = useFormContext();

    const value: FileItem[] = useWatch({ name }) || [];
    const latestRef = useRef<FileItem[]>(value);

    useEffect(() => {
        latestRef.current = value;
    }, [value]);

    function safeSetValue(updater: (prev: FileItem[]) => FileItem[]) {
        const prev = latestRef.current;
        const updated = updater(prev);
        latestRef.current = updated;
        setValue(name, updated);
    }

    const updateFileById = (id: string, update: Partial<FileItem>) => {
        safeSetValue((prev) => prev.map((f) => (f.id === id ? { ...f, ...update } : f)));
    };

    const addFiles = useCallback(
        (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            clearErrors(name);

            const newItems: FileItem[] = acceptedFiles.map((file) => ({
                id: uuid(),
                file,
                fileName: file.name,
                size: file.size,
                fileFormat: file.type,
                uploadStatus: FileItemStatusEnum.PENDING,
            }));

            safeSetValue((prev) => [...prev, ...newItems]);

            newItems.forEach(async (fileItem) => {
                try {
                    const extra = options?.onLoad ? await options.onLoad(fileItem) : await getFileData(fileItem.file);
                    updateFileById(fileItem.id, {
                        ...extra,
                        uploadStatus: extra.uploadStatus || FileItemStatusEnum.DATA_IN_FORM,
                    });
                } catch (err: any) {
                    updateFileById(fileItem.id, {
                        uploadStatus: FileItemStatusEnum.ERROR_ON_UPLOAD,
                        errorMsg: err.message,
                    });
                }
            });

            const uniqueErrorCodes = new Set(rejectedFiles.flatMap((file) => file.errors.map((e) => e.code)));
            uniqueErrorCodes.forEach((errorCode) =>
                setError(name, {
                    type: "manual",
                    message: errorCode,
                }),
            );
        },
        [name, options?.onLoad, setError, clearErrors],
    );

    const removeFile = useCallback(
        async (fileItem: FileItem) => {
            if (options?.onDelete) {
                try {
                    await options.onDelete(fileItem);
                } catch (e) {
                    safeSetValue((prev) =>
                        prev.map((f) => (f.id === fileItem.id ? { ...f, uploadStatus: FileItemStatusEnum.ERROR_ON_DELETE } : f)),
                    );
                    return;
                }
            }

            safeSetValue((prev) => prev.filter((f) => f.id !== fileItem.id));
        },
        [name, options?.onDelete],
    );

    return {
        files: value,
        addFiles,
        removeFile,
    };
}

async function getFileData(file: File): Promise<Partial<FileItem>> {
    const data = await fileUtil.readFileAsBase64(file);
    return { data };
}
